// src/contexts/AuthContext.js
import { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../supabase';
import { useNavigate } from "react-router-dom";
import { subscribeToResendAudience } from '../hooks/useResendAudience';

const AuthContext = createContext({
  user: null,
  session: null,
  loading: true,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  resendAuthEmail: async () => {},
});

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for active session on initial load
    const getSession = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        
        setSession(data.session);
        setUser(data.session?.user || null);
      } catch (error) {
        // Error getting session
      } finally {
        setLoading(false);
      }
    };

    getSession();

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (event === 'SIGNED_OUT') {
          // Clear everything on sign out
          setSession(null);
          setUser(null);
          setLoading(false);
          // Clear storage
          try {
            sessionStorage.clear();
            localStorage.removeItem('sb-zloptqlrfovwujzgytlm-auth-token');
          } catch (e) {
            // Ignore storage errors
          }
        } else {
          setSession(newSession);
          setUser(newSession?.user || null);
          setLoading(false);
        }
      }
    );

    // Cleanup
    return () => {
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);



  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      
      // Subscribe user to Resend audience upon successful sign-in - moved to profile info context
      // if (data?.user && !data?.user.user_metadata.hasOwnProperty('isFirstTimeUser')) {
      //   try {
      //     await subscribeToResendAudience({
      //       email: data.user.email,
      //       firstName: data.user.user_metadata.first_name,
      //       lastName: data.user.user_metadata.last_name,
      //     });

      //     await supabase.auth.updateUser({
      //       data: { isFirstTimeUser: false },
      //     });
      //   } catch (err) {
      //     console.error("Failed to subscribe user to Resend audience:", err);
      //   }
      // }

      setUser(data.user);
      setSession(data.session);
      // Don't navigate here - let LoginPage handle navigation based on onboarding status
      // This allows users to stay on landing pages if they want
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // Sign up with email and password
  const signUp = async (email, password, userData, captchaToken) => {
    try {
      // Get the current origin for the redirect URL
      const currentOrigin = window.location.origin;
      const redirectUrl = `${currentOrigin}/role-selection`;
      
      // Build options object
      // Note: emailRedirectTo is optional and can cause issues if not properly configured in Supabase
      // If email service fails, we'll handle it gracefully
      const signUpOptions = {
        data: userData
      };

      if (typeof captchaToken === "string" && captchaToken.trim()) {
        signUpOptions.captchaToken = captchaToken.trim();
      }
      
      // Add emailRedirectTo for https domains (production: doctorsjobs.io)
      // emailRedirectTo must be in Supabase's allowed redirect URLs list
      if (currentOrigin && 
          !currentOrigin.includes('localhost') && 
          !currentOrigin.includes('127.0.0.1') &&
          currentOrigin.startsWith('https://')) {
        signUpOptions.emailRedirectTo = redirectUrl;
      }
      
      // Try to sign up - even if email fails, user might still be created
      const result = await supabase.auth.signUp({
        email,
        password,
        options: signUpOptions
      });
      
      // Check if user was created (even if there's an error)
      const userWasCreated = result.data?.user !== null && result.data?.user !== undefined;
      
      // If there's an error related to email confirmation
      if (result.error) {
        const isEmailError = result.error.message?.includes('confirmation email') || 
                            result.error.message?.includes('Error sending') ||
                            result.error.message?.includes('email');
        
        if (isEmailError && userWasCreated) {
          // User was created but email failed - this is still a success
          console.warn('User created but email confirmation failed:', result.error.message);
          return {
            data: result.data,
            error: {
              ...result.error,
              message: 'Account created successfully, but there was an issue sending the confirmation email. You can try logging in directly, or contact support if you need help.'
            }
          };
        }
        
        // If it's an email error but user wasn't created, check if user exists anyway
        if (isEmailError && !userWasCreated) {
          // Sometimes Supabase creates the user even when email fails
          // Check if we can find the user by trying to sign in
          try {
            const { data: signInData } = await supabase.auth.signInWithPassword({ email, password });
            if (signInData?.user) {
              // User exists! Sign them out and return success
              await supabase.auth.signOut();
              return {
                data: { user: signInData.user },
                error: {
                  ...result.error,
                  message: 'Account created successfully, but there was an issue sending the confirmation email. You can try logging in directly, or contact support if you need help.'
                }
              };
            }
          } catch (checkError) {
            // User doesn't exist, return the original error
            console.error('Could not verify user creation:', checkError);
          }
        }
      }
      
      return result;
    } catch (error) {
      console.error('Signup error:', error);
      
      // Even if there's an exception, check if user was created
      const isEmailError = error.message?.includes('confirmation email') || 
                          error.message?.includes('Error sending') ||
                          error.message?.includes('email');
      
      if (isEmailError) {
        // Try to verify if user exists
        try {
          const { data: signInData } = await supabase.auth.signInWithPassword({ email, password });
          if (signInData?.user) {
            await supabase.auth.signOut();
            return {
              data: { user: signInData.user },
              error: {
                message: 'Account created successfully, but there was an issue sending the confirmation email. You can try logging in directly, or contact support if you need help.'
              }
            };
          }
        } catch (checkError) {
          // User doesn't exist
        }
      }
      
      return {
        data: null,
        error: {
          message: error.message || 'An error occurred during signup'
        }
      };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      // Clear session from Supabase
      const { error } = await supabase.auth.signOut();
      
      // Clear local state immediately
      setUser(null);
      setSession(null);
      setLoading(false);
      
      // Clear any cached data
      try {
        sessionStorage.clear();
        localStorage.removeItem('sb-zloptqlrfovwujzgytlm-auth-token');
      } catch (e) {
        // Ignore storage errors
      }
      
      // Navigate to home page
      navigate("/", { replace: true });
      
      // Force page reload to ensure complete logout
      window.location.href = "/";
      
      return { error: null };
    } catch (err) {
      // Even if signOut fails, clear local state
      setUser(null);
      setSession(null);
      setLoading(false);
      navigate("/", { replace: true });
      return { error: err };
    }
  };

  // Resend authentication email
  const resendAuthEmail = async (email) => {
    try {
      const { data, error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    resendAuthEmail,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 